import { ConversionRateTypes } from 'constants/conversionTypes';
import {
  EXPORT_TYPE_NAMES,
  ExportTypes,
  ExportJobTypes,
  ExportTransportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';

import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { millisecondsToSeconds } from 'date-fns';
import { useReportExport } from 'hooks/queries/useReportExport';
import { ListContextProvider, useList, useNotify } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { PageHeader } from 'shared/mui/PageHeader';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { cleanEmpty } from 'utils/cleanEmpty';

import { BalanceReportListFilter } from './BalanceReportListFilter';
import { BalanceReportListFilterChips } from './BalanceReportListFilterChips';
import { BalanceReportListContent } from './components/BalanceReportListContent';
import { useBalanceReportList } from './hooks/useBalanceReportList';

export type BalanceReportFilterValues = {
  currency?: string | null;
  conversionRateType?: ConversionRateTypes | undefined;
  periodFrom?: number | null;
  periodTo?: number | null;
  companies?: string[] | [];
  merchants?: string[] | [];
  providers?: string[] | [];
  merchantAccounts?: string[] | [];
  exportType?: ExportType;
  appliedDate?: DateRangeValue;
  timezone?: DateRangeTimezoneValue;
};

const DEFAULT_BALANCE_REPORT_FILTER_VALUES = {
  conversionRateType: null,
  currency: null,
  companies: [],
  merchants: [],
  providers: [],
  merchantAccounts: [],
  appliedDate: [null, null],
  timezone: null,
};

export const BalanceReportList: React.FC = () => {
  const { form, onSubmit, onReset, appliedFilters, setAppliedFilter, openDrawer, toggleDrawer } =
    useFilters<any>({
      defaultValues: DEFAULT_BALANCE_REPORT_FILTER_VALUES,
      resetValues: DEFAULT_BALANCE_REPORT_FILTER_VALUES,
      mode: 'all',
    });

  const { balanceList, isLoading } = useBalanceReportList({
    ...appliedFilters,
    periodFrom: millisecondsToSeconds(appliedFilters?.appliedDate[0]),
    periodTo: millisecondsToSeconds(appliedFilters?.appliedDate[1]),
    timezone: appliedFilters?.timezone,
  });
  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });
  const notify = useNotify();

  const listContext = useList({
    data: balanceList?.items || [],
  });

  const isListFilterExist = Object.keys(cleanEmpty(appliedFilters)).length > 0;

  const { mutate: exportReportMutate, isLoading: isExportLoading } = useReportExport({
    filters: cleanEmpty({
      conversionRateType: appliedFilters.conversionRateType,
      companies: appliedFilters.companies,
      merchants: appliedFilters.merchants,
      providers: appliedFilters.providers,
      merchantAccounts: appliedFilters.merchantAccounts,
      currency: appliedFilters.currency,
      periodFrom: millisecondsToSeconds(appliedFilters.appliedDate[0]),
      periodTo: millisecondsToSeconds(appliedFilters.appliedDate[1]),
      timezone: appliedFilters.timezone || DEFAULT_TIMEZONE,
      exportType: exportType,
      jobType: ExportJobTypes.Balance,
      ...(isSendReportOnEmail && { notificationTransport: [ExportTransportTypes.Email] }),
    }),
    preferenceKey: ReportNames.Balance,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по балансам формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  function submitHandler(values: any) {
    form.clearErrors();
    onSubmit(values);
  }

  return (
    <ListContextProvider value={listContext}>
      <Box my={3}>
        <PageHeader
          actions={[
            <ExportTypesButton
              disabled={!isListFilterExist}
              exportType={exportType}
              key="export-button"
              onChangeType={setExportType}
              onClick={() => setIsOpenNotifyDialog(true)}
            />,
          ]}
          headerTitle={{
            titleText: 'Отчёт о балансах',
            tooltipText:
              'Отчёт о балансах - это сводный отчёт по движению финансовых средств на балансе “Счетов мерчантов” за запрошенный период времени. Обратите внимание, что данный отчёт не отражает фактическое состояние баланса.',
          }}
        />
        <FilterToolbar
          leftActionsSlot={[
            <FilterButton key="filter" onClick={toggleDrawer(true)} />,
            <FilterColumnsButton
              disabled={!balanceList || !isListFilterExist}
              key="select-column"
            />,
          ]}
        />
        <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
          <FormProvider {...form}>
            <form noValidate onSubmit={form.handleSubmit(submitHandler)}>
              <FilterDrawerContent>
                <BalanceReportListFilter />
              </FilterDrawerContent>
              <FilterDrawerActionsButtons>
                <FilterResetButton label="Очистить" onReset={onReset} />
                <FilterApplyButton type="submit" />
              </FilterDrawerActionsButtons>
            </form>
          </FormProvider>
        </FilterDrawer>
        <FilterChipsToolbar appliedFilters={appliedFilters}>
          <FilterContextProvider
            appliedFilters={appliedFilters}
            onReset={onReset}
            setAppliedFilter={setAppliedFilter}
          >
            <FormProvider {...form}>
              <BalanceReportListFilterChips />
              <FilterClearButton key="clear" onClick={onReset} visible={isListFilterExist} />
            </FormProvider>
          </FilterContextProvider>
        </FilterChipsToolbar>
        <BalanceReportListContent
          filters={appliedFilters}
          isLoading={isLoading}
          toggleDrawer={toggleDrawer}
        />
      </Box>
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isExportLoading || isLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportReportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title="Экспорт отчёта"
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListContextProvider>
  );
};
